import { AppLayout } from '@/components/layout/AppLayout';
import { GlassCard } from '@/components/ui/layout/card';
import { CardHeader, CardContent, CardTitle, CardDescription } from '@/components/ui/layout/card';
import { Badge } from '@/components/ui/badge';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { CheckCircle2, AlertTriangle, XCircle, Clock, Activity } from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface PipelineSummary {
  total_runs: number;
  completed: number;
  failed: number;
  running: number;
}

interface PipelineRun {
  id: string;
  user_id: string;
  week_start: string;
  pipeline_type: string;
  status: 'completed' | 'failed' | 'running' | 'pending';
  current_step: string | null;
  steps_completed: number;
  started_at: string;
  error_message: string | null;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const STATUS_META: Record<string, { color: string; Icon: typeof CheckCircle2; label: string }> = {
  completed: { color: 'text-emerald-400', Icon: CheckCircle2, label: 'Completed' },
  failed:    { color: 'text-red-400',     Icon: XCircle,      label: 'Failed' },
  running:   { color: 'text-amber-400',   Icon: Activity,     label: 'Running' },
  pending:   { color: 'text-slate-400',   Icon: Clock,        label: 'Pending' },
};

function statusBadge(status: string) {
  const meta = STATUS_META[status] ?? STATUS_META.pending;
  const { color, Icon, label } = meta;
  return (
    <Badge variant="outline" className={`${color} border-current/30 gap-1`}>
      <Icon className="h-3 w-3" />
      {label}
    </Badge>
  );
}

function formatDatetime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

function truncate(str: string | null | undefined, len: number): string {
  if (!str) return '—';
  return str.length > len ? `${str.slice(0, len)}…` : str;
}

/* ------------------------------------------------------------------ */
/*  Summary stat card                                                  */
/* ------------------------------------------------------------------ */

interface StatCardProps {
  label: string;
  value: number | undefined;
  icon: React.ReactNode;
  accent?: string;
}

function StatCard({ label, value, icon, accent = 'text-white' }: StatCardProps) {
  return (
    <GlassCard>
      <CardContent className="flex items-center gap-4 py-5 px-5">
        <div className="shrink-0 opacity-60">{icon}</div>
        <div className="min-w-0">
          <p className="text-xs font-mono uppercase tracking-wider text-[#A0A0A0]">{label}</p>
          <p className={`text-2xl font-mono font-semibold ${accent}`}>
            {value !== undefined ? value : '—'}
          </p>
        </div>
      </CardContent>
    </GlassCard>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function PipelineHealth() {
  const { data: summary, isLoading: summaryLoading } = useQuery<PipelineSummary>({
    queryKey: ['pipeline_summary'],
    queryFn: async () => {
      const resp = await api.get('/admin/pipeline/summary');
      return resp.data as PipelineSummary;
    },
    refetchInterval: 30000,
  });

  const { data: runs, isLoading: runsLoading } = useQuery<PipelineRun[]>({
    queryKey: ['pipeline_runs'],
    queryFn: async () => {
      const resp = await api.get('/admin/pipeline/runs', { params: { limit: 50 } });
      return resp.data as PipelineRun[];
    },
    refetchInterval: 30000,
  });

  return (
    <AppLayout>
      <div className="space-y-8 max-w-7xl mx-auto">
        {/* ---- Header ---- */}
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-[#E8E8E8]">
            Pipeline Health
          </h1>
          <p className="text-sm text-[#A0A0A0] mt-1 font-mono">
            Weekly closed-loop pipeline execution status
          </p>
        </div>

        {/* ---- Summary Row ---- */}
        {summaryLoading ? (
          <p className="text-sm text-[#A0A0A0] font-mono">Loading summary…</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              label="Total Runs"
              value={summary?.total_runs}
              icon={<Clock className="h-5 w-5 text-[#A0A0A0]" />}
            />
            <StatCard
              label="Completed"
              value={summary?.completed}
              icon={<CheckCircle2 className="h-5 w-5 text-emerald-400" />}
              accent="text-emerald-400"
            />
            <StatCard
              label="Failed"
              value={summary?.failed}
              icon={<XCircle className="h-5 w-5 text-red-400" />}
              accent="text-red-400"
            />
            <StatCard
              label="Running"
              value={summary?.running}
              icon={<Activity className="h-5 w-5 text-amber-400" />}
              accent="text-amber-400"
            />
          </div>
        )}

        {/* ---- Recent Runs Table ---- */}
        <GlassCard>
          <CardHeader>
            <CardTitle className="text-lg text-[#E8E8E8]">Recent Runs</CardTitle>
            <CardDescription className="text-[#A0A0A0] font-mono text-xs">
              Last 50 pipeline executions
            </CardDescription>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {runsLoading ? (
              <p className="text-sm text-[#A0A0A0] font-mono py-4">Loading runs…</p>
            ) : !runs || runs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-[#A0A0A0]">
                <AlertTriangle className="h-8 w-8 mb-3 opacity-40" />
                <p className="text-sm font-mono">No pipeline runs recorded yet.</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/[0.08]">
                    {['Status', 'User ID', 'Week Start', 'Pipeline Type', 'Current Step', 'Steps', 'Started At', 'Error'].map(
                      (h) => (
                        <th
                          key={h}
                          className="text-left text-xs font-mono uppercase tracking-wider text-[#A0A0A0] py-3 px-3 first:pl-0"
                        >
                          {h}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {runs.map((run) => (
                    <tr
                      key={run.id}
                      className="bg-white/[0.02] border-b border-white/[0.05] hover:bg-white/[0.04] transition-colors"
                    >
                      <td className="py-3 px-3 first:pl-0">{statusBadge(run.status)}</td>
                      <td className="py-3 px-3 font-mono text-[#E8E8E8]">
                        {run.user_id ? run.user_id.slice(0, 8) : '—'}
                      </td>
                      <td className="py-3 px-3 font-mono text-[#E8E8E8]">
                        {run.week_start ? formatDate(run.week_start) : '—'}
                      </td>
                      <td className="py-3 px-3 font-mono text-[#A0A0A0]">
                        {run.pipeline_type ?? '—'}
                      </td>
                      <td className="py-3 px-3 font-mono text-[#A0A0A0]">
                        {run.current_step ?? '—'}
                      </td>
                      <td className="py-3 px-3 font-mono text-[#E8E8E8] text-center">
                        {run.steps_completed ?? 0}
                      </td>
                      <td className="py-3 px-3 font-mono text-[#A0A0A0] whitespace-nowrap">
                        {run.started_at ? formatDatetime(run.started_at) : '—'}
                      </td>
                      <td className="py-3 px-3 font-mono text-red-400/80 max-w-[200px]">
                        {run.status === 'failed' ? truncate(run.error_message, 60) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </GlassCard>
      </div>
    </AppLayout>
  );
}
